import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';

const ACCENT = '#10B981';
const CYAN = '#06B6D4';
const AMBER = '#F59E0B';
const TEXT = 'var(--text)';
const MUTED = 'var(--muted)';
const BORDER = 'var(--border)';
const BG2 = 'var(--bg2)';

const MARKETS = [
  {
    flag: '🇦🇪',
    title: 'Dubai/UAE',
    pop: '35 Lakh',
    inv: '₹85 Lakh',
    time: '6PM-9PM IST',
    badge: 'High Potential',
    badgeStyle: { bg: `${AMBER}22`, color: '#FBBF24' },
  },
  {
    flag: '🇬🇧',
    title: 'United Kingdom',
    pop: '18 Lakh',
    inv: '₹1.2 Cr',
    time: '8PM-11PM IST',
    badge: 'High Potential',
    badgeStyle: { bg: `${AMBER}22`, color: '#FBBF24' },
  },
  {
    flag: '🇺🇸',
    title: 'USA/Canada',
    pop: '45 Lakh',
    inv: '₹1.5 Cr',
    time: '9PM-12AM IST',
    badge: 'Premium',
    badgeStyle: { bg: `${ACCENT}22`, color: ACCENT },
  },
  {
    flag: '🌏',
    title: 'Other Countries',
    pop: '1+ Crore',
    inv: '₹50 Lakh',
    time: 'AI detects',
    badge: 'Growing',
    badgeStyle: { bg: `${CYAN}22`, color: '#67E8F9' },
  },
] as const;

const CLOCKS = [
  { label: '🇦🇪 Dubai', tz: 'Asia/Dubai' },
  { label: '🇬🇧 London', tz: 'Europe/London' },
  { label: '🇺🇸 New York', tz: 'America/New_York' },
  { label: '🇸🇬 Singapore', tz: 'Asia/Singapore' },
] as const;

const BIZ_MARKETS = [
  { name: 'Dubai / UAE', tz: 'Asia/Dubai' },
  { name: 'United Kingdom', tz: 'Europe/London' },
  { name: 'USA (East)', tz: 'America/New_York' },
  { name: 'Singapore', tz: 'Asia/Singapore' },
] as const;

function localHour(tz: string): number {
  const parts = new Intl.DateTimeFormat('en', { timeZone: tz, hour: 'numeric', hourCycle: 'h23' }).formatToParts(
    new Date()
  );
  const h = parts.find((p) => p.type === 'hour');
  return h ? parseInt(h.value, 10) : 0;
}

function formatTime(tz: string): string {
  return new Date().toLocaleTimeString('en-IN', { timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
}

export function NriMarketingPage() {
  const [now, setNow] = useState(() => new Date());
  const [targetMarket, setTargetMarket] = useState('Dubai');
  const [campType, setCampType] = useState('Real Estate');
  const [budget, setBudget] = useState('₹25,000-50,000');
  const [message, setMessage] = useState('');
  const [usps, setUsps] = useState('');
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(false);
  const [city, setCity] = useState('Moradabad');

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const { data } = await api.get<{ business: { city: string | null } | null }>('/business/me');
        if (data.business?.city) setCity(data.business.city);
      } catch {
        /* demo */
      }
    })();
  }, []);

  const bestMarkets = BIZ_MARKETS.filter((m) => {
    const h = localHour(m.tz);
    return h >= 9 && h < 18;
  });
  const bestLabel =
    bestMarkets.length > 0 ? bestMarkets.map((m) => m.name).join(' · ') : 'Off-hours — next window: check Dubai evening (IST)';

  const generate = useCallback(async () => {
    setGenerating(true);
    setPreview(false);
    await new Promise((r) => setTimeout(r, 2000));
    setPreview(true);
    setGenerating(false);
  }, []);

  return (
    <div className="odc-page relative z-10 space-y-8">
      <header
        className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between"
        style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 24 }}
      >
        <div className="min-w-0 flex-1 space-y-3">
          <span
            className="inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{ background: `${CYAN}22`, color: '#67E8F9', border: `1px solid ${CYAN}55` }}
          >
            NRI MARKETING
          </span>
          <h1 className="text-2xl font-bold md:text-3xl" style={{ color: TEXT }}>
            Reach NRI Investors Worldwide
          </h1>
          <p className="max-w-xl text-sm leading-relaxed" style={{ color: MUTED }}>
            Dubai, UK, USA, Canada mein rehne wale Indians target karein. High-ticket deals, untapped goldmine.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {['3.5 Crore NRIs Worldwide', '5x Higher Ticket Size', 'Auto Time-Zone Adjust'].map((t) => (
            <span
              key={t}
              className="shrink-0 rounded-full border px-3 py-2 text-[11px] font-semibold"
              style={{ borderColor: `${CYAN}44`, background: `${CYAN}12`, color: '#67E8F9' }}
            >
              {t}
            </span>
          ))}
        </div>
      </header>

      <section className="odc-card !p-6" style={{ transform: 'none' }}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {MARKETS.map((m) => (
            <div key={m.title} className="rounded-xl border p-4" style={{ borderColor: BORDER, background: BG2 }}>
              <div className="text-2xl">{m.flag}</div>
              <h3 className="mt-2 text-sm font-bold" style={{ color: TEXT }}>
                {m.title}
              </h3>
              <p className="mt-2 text-xs" style={{ color: MUTED }}>
                NRI Population: <span style={{ color: TEXT }}>{m.pop}</span>
              </p>
              <p className="text-xs" style={{ color: MUTED }}>
                Avg Investment: <span style={{ color: TEXT }}>{m.inv}</span>
              </p>
              <p className="text-xs" style={{ color: MUTED }}>
                Best Time: <span style={{ color: TEXT }}>{m.time}</span>
              </p>
              <span
                className="mt-2 inline-block rounded-md px-2 py-0.5 text-[9px] font-bold uppercase"
                style={{ background: m.badgeStyle.bg, color: m.badgeStyle.color }}
              >
                {m.badge}
              </span>
            </div>
          ))}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
        <div className="odc-card space-y-4 !p-6" style={{ transform: 'none' }}>
          <h2 className="text-lg font-bold" style={{ color: TEXT }}>
            Create NRI Campaign
          </h2>
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Target Market
            </p>
            <div className="flex flex-wrap gap-2">
              {(['Dubai', 'UK', 'USA', 'All Markets'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setTargetMarket(m)}
                  className="rounded-full border px-3 py-1.5 text-xs font-semibold"
                  style={{
                    borderColor: targetMarket === m ? ACCENT : BORDER,
                    background: targetMarket === m ? `${ACCENT}22` : BG2,
                    color: targetMarket === m ? TEXT : MUTED,
                  }}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Campaign Type
            </p>
            <div className="flex flex-wrap gap-2">
              {(['Real Estate', 'Investment', 'Business', 'Education'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setCampType(m)}
                  className="rounded-full border px-3 py-1.5 text-xs font-semibold"
                  style={{
                    borderColor: campType === m ? ACCENT : BORDER,
                    background: campType === m ? `${ACCENT}22` : BG2,
                    color: campType === m ? TEXT : MUTED,
                  }}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Budget Range
            </span>
            <select className="odc-select mt-1.5 w-full !py-2.5" value={budget} onChange={(e) => setBudget(e.target.value)}>
              <option>₹10,000-25,000</option>
              <option>₹25,000-50,000</option>
              <option>₹50,000-1,00,000</option>
              <option>Custom</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Key Message
            </span>
            <textarea
              className="odc-input mt-1.5 min-h-[5rem] w-full"
              placeholder="e.g. Invest in your roots. Premium plots in your hometown..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              USPs
            </span>
            <input
              className="odc-input mt-1.5 w-full"
              placeholder="e.g. RERA registered, 12% returns, EMI available"
              value={usps}
              onChange={(e) => setUsps(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="odc-btn-primary w-full justify-center !py-3"
            disabled={generating}
            onClick={() => void generate()}
          >
            {generating ? 'Generating…' : '🌍 Generate NRI Campaign'}
          </button>
        </div>

        <div className="odc-card !flex !min-h-[280px] !flex-col !p-6" style={{ transform: 'none' }}>
          <h2 className="text-lg font-bold" style={{ color: TEXT }}>
            Campaign Preview
          </h2>
          {!preview ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-sm" style={{ color: MUTED }}>
              Select options and generate
            </div>
          ) : (
            <div className="mt-4 space-y-3 text-xs leading-relaxed" style={{ color: MUTED }}>
              <div className="rounded-xl border p-3" style={{ borderColor: BORDER, background: BG2 }}>
                <strong style={{ color: TEXT }}>📱 Facebook Ad (NRI Groups):</strong>
                <br />
                Dreaming of home? 🏠 Invest in {city} premium plots. RERA registered. EMI from ₹15,000/month. NRI special:
                Zero brokerage + free site visit on your next India trip.
              </div>
              <div className="rounded-xl border p-3" style={{ borderColor: BORDER, background: BG2 }}>
                <strong style={{ color: TEXT }}>💬 WhatsApp Message (IST adjusted for Dubai):</strong>
                <br />
                Hello! If you are based in Dubai and planning to invest in India, our special NRI package is designed for you
                dekhein...
              </div>
              <div className="rounded-xl border p-3" style={{ borderColor: BORDER, background: BG2 }}>
                <strong style={{ color: TEXT }}>📧 Email Subject:</strong>
                <br />
                For NRIs: Premium plots in {city} — RERA registered, 12% returns
              </div>
            </div>
          )}
        </div>
      </div>

      <section className="odc-card !p-6" style={{ transform: 'none' }}>
        <h2 className="text-lg font-bold" style={{ color: TEXT }}>
          Smart Time-Zone Calling
        </h2>
        <p className="mt-1 text-sm" style={{ color: MUTED }}>
          AI automatically calls NRIs at the right time in their timezone
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {CLOCKS.map((c) => (
            <div key={c.tz} className="rounded-xl border p-4 text-center" style={{ borderColor: BORDER, background: BG2 }}>
              <p className="text-sm font-bold" style={{ color: TEXT }}>
                {c.label}
              </p>
              <p className="odc-metric mt-2 !text-lg" style={{ color: CYAN, fontVariantNumeric: 'tabular-nums' }}>
                {formatTime(c.tz)}
              </p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-sm font-semibold" style={{ color: TEXT }}>
          Current best market to call:{' '}
          <span style={{ color: ACCENT }}>{bestLabel}</span>
        </p>
        <p className="text-center text-[10px]" style={{ color: MUTED }}>
          Based on 9AM–6PM local business hours ({now.toLocaleTimeString()})
        </p>
      </section>
    </div>
  );
}
